import { DeleteOutline, Refresh, Watch } from "@mui/icons-material";
import {
  alpha,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import dayjs, { type Dayjs } from "dayjs";
import { useMemo, useState } from "react";
import { buildWearableRemovableItems } from "./wearableRemovableItems";
import { isMobile } from "react-device-detect";
import { useNavigate } from "react-router-dom";
import { LargeContentBox } from "@/components/common";
import { PageHeader } from "@/components/common/PageHeader";
import { ScreenMobile } from "@/components/common/ScreenMobile";
import {
  FLUXHEALTH_RANGE_PRESETS,
  isValidFluxHealthRangeDays,
  type FluxHealthRangeDays,
  WearableDateRangeChips,
} from "@/pages/medicalcare/components/Wearable/WearableDateRangeChips";
import { WearablePeriodDateRow } from "@/pages/medicalcare/components/Wearable/WearablePeriodDateRow";
import {
  formatWearablePeriodLabel,
  resolveWearablePeriod,
} from "@/pages/medicalcare/components/Wearable/wearableMetricAggregation";
import { useSessionContext } from "@/contexts/SessionContext";
import { WearableEmptyState } from "./components/WearableEmptyState";
import {
  ObservationSection,
  SECTION_META,
} from "./components/ObservationSection";
import { PatientNoteSection } from "./components/PatientNoteSection";
import {
  PeriodChartStyleToggle,
  type PeriodChartStyle,
} from "./components/PeriodChartStyleToggle";
import {
  collectAwakeningRecords,
  getLatestPatientNoteText,
} from "./observationNotes";
import { PeriodTrendsView } from "./components/PeriodTrendsView";
import { WearableRemoveDayDataDialog } from "./components/WearableRemoveDayDataDialog";
import { useWearableObservationsData } from "./useWearableObservationsData";

interface WearableObservationsProps {
  open?: boolean;
  onClose?: () => void;
  /** Sem PageHeader/ScreenMobile (pai controla o shell). */
  embedded?: boolean;
  /**
   * CPF do paciente no atendimento. Se omitido, usa username da sessão
   * (fluxo do próprio paciente).
   */
  patientId?: string;
  /** Sem o card de passos FluxHealth / Samsung Health no estado vazio. */
  hideSetupInstructions?: boolean;
}

/**
 * Observações FluxHealth. Intervalos: 1 / 7 / 15 / 30 dias.
 * Em multi-dia: visão linha (padrão) ou barras — um único toggle abaixo do filtro.
 */
export function WearableObservations({
  open,
  onClose,
  embedded = false,
  patientId: patientIdProp,
  hideSetupInstructions = false,
}: WearableObservationsProps) {
  const navigate = useNavigate();
  const { user } = useSessionContext();
  const patientId = patientIdProp || user?.username;

  const today = useMemo(() => dayjs().startOf("day"), []);
  const [selectedDate, setSelectedDate] = useState<Dayjs>(() =>
    dayjs().startOf("day")
  );
  const [rangeDays, setRangeDays] = useState<FluxHealthRangeDays>(1);
  const [chartStyle, setChartStyle] = useState<PeriodChartStyle>("line");
  const [removeDayDialogOpen, setRemoveDayDialogOpen] = useState(false);

  const { periodStart, periodEnd } = useMemo(
    () => resolveWearablePeriod({ selectedDate, rangeDays, today }),
    [selectedDate, rangeDays, today]
  );
  const periodLabel = formatWearablePeriodLabel(
    periodStart,
    periodEnd,
    rangeDays
  );
  const isToday = periodEnd.isSame(today, "day");

  const {
    loading,
    isCancelling,
    filteredObservations,
    cancellableObservations,
    cancellableObservationIds,
    cancelObservations,
    issuedGroups,
    selectedIssuedTime,
    setSelectedIssuedTime,
    buckets,
    deviceNameOf,
    refetch,
  } = useWearableObservationsData(
    patientId,
    periodStart,
    periodEnd,
    rangeDays
  );

  /** Visível na visão 1 dia; desabilitado sem dados canceláveis ou durante loading. */
  const showRemoveDayDataButton = rangeDays === 1 && Boolean(patientId);
  const canRemoveDayData =
    showRemoveDayDataButton &&
    !loading &&
    cancellableObservationIds.length > 0;

  const removableItems = useMemo(
    () => buildWearableRemovableItems(cancellableObservations),
    [cancellableObservations],
  );

  const handleConfirmRemoveDayData = async (selectedIds: string[]) => {
    try {
      await cancelObservations(selectedIds);
      setRemoveDayDialogOpen(false);
    } catch {
      // Toast já exibido pelo serviço de patch.
    }
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
      return;
    }
    navigate("/home");
  };

  const changeDay = (delta: number) => {
    const candidate = selectedDate.add(delta, "day");
    setSelectedDate(candidate.isAfter(today, "day") ? today : candidate);
  };

  const pickDate = (date: Dayjs) => {
    const next = date.isAfter(today, "day") ? today : date.startOf("day");
    setSelectedDate(next);
  };

  const openDayDetails = (dateKey: string) => {
    const date = dayjs(dateKey);
    if (!date.isValid()) return;
    pickDate(date);
    setRangeDays(1);
  };

  const sections = SECTION_META.map((meta) => ({
    ...meta,
    items: buckets[meta.id],
    deviceName: deviceNameOf(buckets[meta.id]),
  })).filter((section) => section.items.length > 0);

  // Lidos de todas as Observations do dia: a nota pode vir em qualquer envio
  const awakeningRecords = useMemo(
    () => collectAwakeningRecords(filteredObservations),
    [filteredObservations]
  );
  // Em "Todos": a última nota do dia. Em um horário: a nota daquele envio.
  const patientNoteText = useMemo(() => {
    const noteEntries =
      selectedIssuedTime === "all"
        ? filteredObservations
        : (issuedGroups.find((group) => group.time === selectedIssuedTime)
            ?.items ?? []);
    return getLatestPatientNoteText(noteEntries);
  }, [filteredObservations, issuedGroups, selectedIssuedTime]);

  /** Faixa verde só com período + data (sem toggle de gráfico — evita barra esmagada). */
  const dateHeader = (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 1, sm: 1.5 },
        backgroundColor: "primary800.main",
        color: "white",
        borderRadius: 2,
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        // No atendimento (flex column) sem isso a barra encolhe e some o conteúdo
        flexShrink: 0,
      }}
    >
      <Stack spacing={1.25} sx={{ width: "100%", minWidth: 0 }}>
        <Box
          sx={{
            width: "100%",
            minWidth: 0,
            overflowX: "auto",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          <WearableDateRangeChips
            rangeDays={rangeDays}
            onChange={(days) => {
              if (isValidFluxHealthRangeDays(days)) setRangeDays(days);
            }}
            disabled={loading}
            tone="onPrimary"
            presets={FLUXHEALTH_RANGE_PRESETS}
          />
        </Box>

        <Box
          sx={{
            width: "100%",
            minWidth: 0,
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: showRemoveDayDataButton ? "1fr auto 1fr" : "1fr",
            },
            gridTemplateRows: {
              xs: showRemoveDayDataButton ? "auto auto" : "auto",
              md: "auto",
            },
            alignItems: "center",
            columnGap: 0.75,
            rowGap: 1,
          }}
        >
          {/* Coluna esquerda vazia equilibra o botão à direita e mantém o centro no meio. */}
          {showRemoveDayDataButton ? (
            <Box sx={{ display: { xs: "none", md: "block" }, minWidth: 0 }} />
          ) : null}

          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            justifyContent="center"
            flexWrap="nowrap"
            useFlexGap
            sx={{
              minWidth: 0,
              width: { xs: "100%", md: "max-content" },
              maxWidth: "100%",
              justifySelf: "center",
              gridColumn: {
                xs: "1",
                md: showRemoveDayDataButton ? "2" : "1",
              },
              gridRow: { xs: "1", md: "1" },
              ...(showRemoveDayDataButton
                ? { minWidth: { md: 480 } }
                : {}),
            }}
          >
            <Box
              sx={{
                minWidth: 0,
                flex: "1 1 auto",
                maxWidth: { xs: "100%", md: 340 },
              }}
            >
              <WearablePeriodDateRow
                periodLabel={periodLabel}
                periodEnd={periodEnd}
                today={today}
                loading={loading}
                onPrevDay={() => changeDay(-1)}
                onNextDay={() => changeDay(1)}
                onPickDate={pickDate}
              />
            </Box>
            <Button
              size="small"
              variant="outlined"
              disabled={loading || isToday}
              onClick={() => pickDate(today)}
              sx={{
                minWidth: 56,
                flexShrink: 0,
                textTransform: "none",
                fontWeight: 700,
                fontSize: "0.8125rem",
                color: "white",
                borderColor: (t) => alpha(t.palette.common.white, 0.4),
                bgcolor: (t) => alpha(t.palette.common.white, 0.08),
                "&:hover": {
                  borderColor: (t) => alpha(t.palette.common.white, 0.65),
                  bgcolor: (t) => alpha(t.palette.common.white, 0.16),
                },
                "&.Mui-disabled": {
                  color: (t) => alpha(t.palette.common.white, 0.32),
                  borderColor: (t) => alpha(t.palette.common.white, 0.14),
                },
              }}
            >
              Hoje
            </Button>
            <Tooltip title="Atualizar dados do período">
              <span>
                <IconButton
                  size="small"
                  onClick={refetch}
                  disabled={loading || isCancelling || !patientId}
                  aria-label="Atualizar observações do período"
                  sx={{
                    color: "white",
                    border: "1px solid",
                    borderColor: (t) => alpha(t.palette.common.white, 0.4),
                    bgcolor: (t) => alpha(t.palette.common.white, 0.08),
                    "&:hover": {
                      borderColor: (t) => alpha(t.palette.common.white, 0.65),
                      bgcolor: (t) => alpha(t.palette.common.white, 0.16),
                    },
                    "&.Mui-disabled": {
                      color: (t) => alpha(t.palette.common.white, 0.32),
                      borderColor: (t) => alpha(t.palette.common.white, 0.14),
                    },
                  }}
                >
                  <Refresh fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>

          {showRemoveDayDataButton ? (
            <Tooltip
              title={
                canRemoveDayData
                  ? "Remover todos os dados deste dia"
                  : "Nenhum dado ativo para remover neste dia"
              }
            >
              <Box
                sx={{
                  gridColumn: { xs: "1", md: "3" },
                  gridRow: { xs: "2", md: "1" },
                  justifySelf: { xs: "stretch", md: "end" },
                  display: "flex",
                  justifyContent: { xs: "center", sm: "flex-end" },
                  width: "100%",
                  flexShrink: 0,
                  pt: { xs: 0.25, md: 0 },
                }}
              >
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={
                    <DeleteOutline
                      fontSize="small"
                      sx={{ color: "error.main" }}
                    />
                  }
                  onClick={() => setRemoveDayDialogOpen(true)}
                  disabled={!canRemoveDayData || isCancelling}
                  aria-label="Remover dados do dia"
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.8125rem",
                    lineHeight: 1.2,
                    width: { xs: "100%", sm: "auto" },
                    maxWidth: { xs: "100%", md: "none" },
                    color: "error.main",
                    borderColor: (t) => alpha(t.palette.error.main, 0.55),
                    bgcolor: (t) => alpha(t.palette.common.white, 0.94),
                    borderRadius: 2,
                    px: 1.5,
                    py: 0.625,
                    boxShadow: (t) =>
                      `0 1px 6px ${alpha(t.palette.error.main, 0.18)}`,
                    "& .MuiButton-startIcon": {
                      mr: 0.5,
                    },
                    "&:hover": {
                      color: "error.main",
                      borderColor: (t) => alpha(t.palette.error.main, 0.55),
                      bgcolor: "neutral300.main",
                      boxShadow: "none",
                      "& .MuiButton-startIcon svg": {
                        color: "error.main",
                      },
                    },
                    "&.Mui-disabled": {
                      color: (t) => alpha(t.palette.error.main, 0.38),
                      borderColor: (t) => alpha(t.palette.error.main, 0.22),
                      bgcolor: (t) => alpha(t.palette.common.white, 0.55),
                      boxShadow: "none",
                      "& .MuiButton-startIcon svg": {
                        color: (t) => alpha(t.palette.error.main, 0.38),
                      },
                    },
                  }}
                >
                  Remover dados do dia
                </Button>
              </Box>
            </Tooltip>
          ) : null}
        </Box>
      </Stack>
    </Paper>
  );

  const issuedTimeChips =
    rangeDays === 1 && issuedGroups.length > 0 ? (
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{
          overflowX: "auto",
          overflowY: "hidden",
          minWidth: 0,
          width: "100%",
          maxWidth: "100%",
          pb: 0.25,
          WebkitOverflowScrolling: "touch",
          overscrollBehaviorX: "contain",
        }}
      >
        <Typography
          variant="body2"
          sx={{ color: "text.secondary", flexShrink: 0 }}
        >
          Envio
        </Typography>
        <Button
          size="small"
          variant={selectedIssuedTime === "all" ? "contained" : "outlined"}
          color="primary"
          onClick={() => setSelectedIssuedTime("all")}
          sx={{ flexShrink: 0, borderRadius: 5, px: 2 }}
        >
          Todos
        </Button>
        {issuedGroups.map((group) => (
          <Button
            key={group.time}
            size="small"
            variant={
              selectedIssuedTime === group.time ? "contained" : "outlined"
            }
            color="primary"
            onClick={() => setSelectedIssuedTime(group.time)}
            sx={{ flexShrink: 0, borderRadius: 5, px: 2 }}
          >
            {group.time}
          </Button>
        ))}
      </Stack>
    ) : null;

  const periodCharts = (
    <PeriodTrendsView
      entries={filteredObservations}
      rangeDays={rangeDays}
      periodEndKey={periodEnd.format("YYYY-MM-DD")}
      chartStyle={chartStyle}
      chartStyleControl={
        <PeriodChartStyleToggle
          value={chartStyle}
          onChange={setChartStyle}
          disabled={loading}
        />
      }
      onSelectDay={openDayDetails}
    />
  );

  const body = loading ? (
    <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 240 }}>
      <CircularProgress />
    </Stack>
  ) : filteredObservations.length === 0 ? (
    <WearableEmptyState
      rangeDays={rangeDays}
      audience={embedded && !hideSetupInstructions ? "professional" : "patient"}
      hideSetupInstructions={hideSetupInstructions}
    />
  ) : rangeDays !== 1 ? (
    <Stack spacing={1.5} sx={{ width: "100%", minWidth: 0 }}>
      {periodCharts}
    </Stack>
  ) : (
    <Stack
      spacing={2}
      sx={{ width: "100%", maxWidth: "100%", minWidth: 0 }}
    >
      {issuedTimeChips}
      {sections.map((section) => (
        <ObservationSection
          key={section.id}
          title={section.title}
          Icon={section.Icon}
          deviceName={section.deviceName}
          items={section.items}
          awakeningRecords={
            section.id === "sleep" ? awakeningRecords : undefined
          }
        />
      ))}
      {/* Nota do paciente é a última seção da página */}
      {patientNoteText ? (
        <PatientNoteSection noteText={patientNoteText} />
      ) : null}
    </Stack>
  );

  const content = (
    <>
    <Stack
      spacing={2}
      sx={{
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        overflowX: "hidden",
        p: embedded ? { xs: 1, sm: 1.25 } : 0,
        // Embutido no atendimento (60vh + flex): barra fixa, corpo com scroll
        ...(embedded
          ? {
              flex: 1,
              minHeight: 0,
              height: "100%",
              overflow: "hidden",
            }
          : {}),
      }}
    >
      {dateHeader}
      <Box
        sx={
          embedded
            ? {
                flex: 1,
                minHeight: 0,
                minWidth: 0,
                overflowY: "auto",
                overflowX: "hidden",
                WebkitOverflowScrolling: "touch",
              }
            : { width: "100%", minWidth: 0 }
        }
      >
        {body}
      </Box>
    </Stack>
    <WearableRemoveDayDataDialog
      open={removeDayDialogOpen}
      periodLabel={periodLabel}
      items={removableItems}
      audience={
        embedded && !hideSetupInstructions ? "professional" : "patient"
      }
      loading={isCancelling}
      onClose={() => setRemoveDayDialogOpen(false)}
      onConfirm={handleConfirmRemoveDayData}
    />
    </>
  );

  if (embedded) {
    return content;
  }

  if (isMobile) {
    return (
      <ScreenMobile
        title="Dados Wearable"
        open={open ?? true}
        onClose={handleClose}
      >
        <Box
          sx={{
            p: { xs: 1.5, sm: 2 },
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
            overflowX: "hidden",
          }}
        >
          {content}
        </Box>
      </ScreenMobile>
    );
  }

  return (
    <LargeContentBox>
      <Stack
        spacing={2}
        sx={{
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          justifySelf: "stretch",
          overflowX: "hidden",
        }}
      >
        <PageHeader
          title="Dados Wearable"
          icon={Watch}
          description="Visualize métricas de saúde e fitness coletadas pelo FluxHealth"
     
        />
        {content}
      </Stack>
    </LargeContentBox>
  );
}
